import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/movies", label: "Movies", id: "movies" },
  { to: "/tv", label: "TV Series", id: "tv" },
  { to: "/about", label: "About", id: "about" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2.5">
    <span className="grid h-9 w-9 place-items-center rounded-full border border-[#D4AF37]/60 font-display text-sm font-bold text-[#D4AF37] transition-colors duration-300 group-hover:bg-[#D4AF37] group-hover:text-black">
      ES
    </span>
    <span className="font-display text-lg font-semibold tracking-tight text-white sm:text-xl">
      Exclusive <span className="text-[#D4AF37]">Streams</span>
    </span>
  </Link>
);

const QuickSearch = ({ testId, onDone, className = "" }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/movies?q=${encodeURIComponent(q.trim())}`);
    setQ("");
    onDone?.();
  };
  return (
    <form onSubmit={submit} className={`relative ${className}`} data-testid={`${testId}-form`}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search movies..."
        data-testid={testId}
        className="h-10 w-full rounded-full border border-white/10 bg-white/5 pl-10 pr-4 text-sm text-white placeholder:text-neutral-500 transition-colors duration-300 focus:border-[#D4AF37]/70 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
      />
    </form>
  );
};

const linkCls = ({ isActive }) =>
  `nav-link relative text-sm font-medium tracking-wide transition-colors duration-300 ${isActive ? "text-[#D4AF37] is-active" : "text-neutral-300 hover:text-white"}`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header data-testid="navbar" className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 md:h-20 md:px-10">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <NavLink key={l.id} to={l.to} end={l.to === "/"} className={linkCls} data-testid={`nav-link-${l.id}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <QuickSearch testId="nav-search-input" className="hidden w-56 lg:block xl:w-64" />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="mobile-menu border-t border-white/10 bg-[#0a0a0a]/95 px-5 pb-8 pt-4 backdrop-blur-xl md:hidden">
          <QuickSearch testId="mobile-search-input" onDone={() => setOpen(false)} />
          <nav className="mt-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <NavLink key={l.id} to={l.to} end={l.to === "/"} className={linkCls} data-testid={`mobile-nav-link-${l.id}`}>
                <span className="font-display text-2xl">{l.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};
